"use client"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import {
  fetchSkillItemArtRecords,
  isVerticalShape,
  resolveSkillIcon,
  type SkillItemArtRecord,
  type SkillItemArtShape,
} from "@/lib/skill-item-art"
import { getShapeDimensions } from "@/lib/grid-logic"

export function SkillArtIcon({
  skillId,
  category,
  skillMdSize,
  shape,
  className,
  label,
}: {
  skillId: string
  category?: string | null
  skillMdSize?: number | null
  /**
   * Display shape for the tile. The resolved asset is always the
   * horizontal-shape record for the skill's size tier; vertical display
   * shapes (1x2 / 1x3) rotate that same asset 90° in place.
   */
  shape?: SkillItemArtShape
  className?: string
  label?: string
}) {
  const [iconUrl, setIconUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    fetchSkillItemArtRecords()
      .then((records: SkillItemArtRecord[]) => {
        if (!active) return
        const art = resolveSkillIcon({
          skillId,
          category,
          skillMdSize,
          records,
        })
        setIconUrl(art?.imageUrl ?? null)
      })
      .catch(() => {
        if (active) {
          setIconUrl(null)
        }
      })
    return () => {
      active = false
    }
  }, [category, skillId, skillMdSize])

  const rotated = shape ? isVerticalShape(shape) : false
  const { cols, rows } = shape
    ? getShapeDimensions(shape)
    : { cols: 1, rows: 1 }

  const backgroundStyle = iconUrl
    ? {
        backgroundImage: `url(${iconUrl})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
      }
    : undefined

  return (
    <div
      aria-label={label ?? skillId}
      className={cn(
        "relative flex items-center justify-center overflow-hidden rounded-none border border-border bg-card/30",
        className
      )}
    >
      {iconUrl ? (
        rotated ? (
          // Container layout is cols x rows (vertical, e.g. 1x2). The inner
          // paint box is sized to the rotated aspect (rows x cols), centered,
          // and rotated 90° around center so a horizontal asset fills it.
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: `${(rows / cols) * 100}%`,
              height: `${(cols / rows) * 100}%`,
              transform: "translate(-50%, -50%) rotate(90deg)",
              transformOrigin: "center center",
              ...backgroundStyle,
            }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, ...backgroundStyle }} />
        )
      ) : (
        <span className="text-[10px] text-muted-foreground uppercase">Icon</span>
      )}
    </div>
  )
}
